'use es6';

import _objectWithoutPropertiesLoose from "@babel/runtime/helpers/esm/objectWithoutPropertiesLoose";
import { combineReducers } from 'redux';
import SeamlessImmutable from 'seamless-immutable';
import { mapObject } from 'underscore';
import { UNINITIALIZED, SUCCEEDED, FAILED, PENDING } from 'DesignData/enums/RequestStatus';
import FolderModel from 'DesignData/models/finder/Folder';
import { FOLDER } from 'DesignData/constants/FolderAssetTypes';
import { HUBSPOT_FOLDER_ID, HUBSPOT_FOLDER_NAME, HUBSPOT_FOLDER_PATH } from 'DesignData/constants/Folders';
import { EMPTY_OBJECT } from 'DesignData/lib/object';
import { createFolderContents, getFoldersByIdFromFolderContentsTree, getFolderContentsByIdFromFolderContentsObject, getFolderMetadataByIdFromFolderContentsTree } from 'DesignData/lib/normalization/finder/folderHelpers';
import { FINDER_FOLDERS_COLLAPSE_ALL, FINDER_FILE_CREATE, FINDER_FILE_CREATE_FROM_EDITOR, FINDER_FILE_DELETE, FINDER_FILE_DUPLICATE, FINDER_FILE_RESTORE_DELETED, FINDER_FOLDER_CREATE, FINDER_FOLDER_DELETE, FINDER_FOLDER_DUPLICATE, FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE, FINDER_FILE_DUPLICATE_READ_ONLY, FINDER_FOLDER_RENAME, FINDER_FOLDER_CONTENTS_BULK_DELETE, FINDER_FOLDER_CONTENTS_FETCH, FINDER_FOLDER_CONTENTS_MOVE, FINDER_FOLDER_TOGGLE_OPEN, FINDER_FOLDER_TOGGLE_LOCK, FINDER_OPEN_FOLDERS_FETCH, FINDER_ROOT_FOLDER_CONTENTS_FETCH, FINDER_ASSETS_SEARCH_FETCH, FINDER_FOLDER_OPEN_DEEPLY_NESTED, FINDER_FILEMAPPER_FETCH_DEFAULT } from 'DesignData/actions/ActionTypes';

function rootFolderId(state = null, action) {
  switch (action.type) {
    case FINDER_ROOT_FOLDER_CONTENTS_FETCH:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        return contents.folder.id;
      }

    default:
      {
        return state;
      }
  }
}

function rootFolderContentsRequestStatus(state = UNINITIALIZED, action) {
  switch (action.type) {
    case FINDER_ROOT_FOLDER_CONTENTS_FETCH:
      {
        if (action.contents) {
          return SUCCEEDED;
        } else if (action.error) {
          return FAILED;
        }

        return PENDING;
      }

    default:
      return state;
  }
}

function fetchRootFolderContentsError(state = null, action) {
  switch (action.type) {
    case FINDER_ROOT_FOLDER_CONTENTS_FETCH:
      {
        return action.error || null;
      }

    default:
      {
        return state;
      }
  }
}

function mergeFolders(state, folders, parentFolder) {
  const foldersById = folders.reduce((hash, folder) => {
    const current = state[folder.id] || null;
    hash[folder.id] = new FolderModel(Object.assign({}, current, {}, folder));
    return hash;
  }, {});

  if (parentFolder && !state[parentFolder.id]) {
    foldersById[parentFolder.id] = new FolderModel(parentFolder);
  }

  return SeamlessImmutable.merge(state, foldersById, {
    deep: true
  });
}

export function byId(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case FINDER_FILEMAPPER_FETCH_DEFAULT:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        const {
          folder,
          subfolders
        } = contents;

        if (state[folder.id]) {
          state = SeamlessImmutable.set(state, folder.id, new FolderModel(Object.assign({}, folder, {}, state[folder.id])));
        } else {
          state = SeamlessImmutable.set(state, folder.id, new FolderModel(Object.assign({}, folder, {
            readOnly: true
          })));
        }

        subfolders.forEach(subfolder => {
          state = SeamlessImmutable.set(state, subfolder.id, new FolderModel(Object.assign({}, subfolder, {
            readOnly: true
          })));
        });
        return state;
      }

    case FINDER_FOLDER_CONTENTS_FETCH:
    case FINDER_ROOT_FOLDER_CONTENTS_FETCH:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        const foldersById = getFoldersByIdFromFolderContentsTree(contents);
        const foldersByIdImmutable = mapObject(foldersById, folder => {
          return new FolderModel(folder);
        }); // Set @hubspot folder parentId

        if (action.type === FINDER_ROOT_FOLDER_CONTENTS_FETCH) {
          if (state[HUBSPOT_FOLDER_ID]) {
            state = SeamlessImmutable.setIn(state, [HUBSPOT_FOLDER_ID, 'parentFolderId'], contents.folder.id);
          } else {
            const model = new FolderModel({
              id: HUBSPOT_FOLDER_ID,
              name: HUBSPOT_FOLDER_NAME,
              readOnly: true,
              fullPath: HUBSPOT_FOLDER_PATH,
              parentFolderId: contents.folder.id
            });
            state = SeamlessImmutable.set(state, HUBSPOT_FOLDER_ID, model);
          }
        }

        return SeamlessImmutable.merge(state, foldersByIdImmutable, {
          deep: true
        });
      }

    case FINDER_OPEN_FOLDERS_FETCH:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        const foldersById = contents.reduce((hash, currFolderContents) => {
          const currFoldersById = getFoldersByIdFromFolderContentsTree(currFolderContents);
          return Object.assign({}, hash, {}, currFoldersById);
        }, {});
        const foldersByIdImmutable = mapObject(foldersById, folder => {
          return new FolderModel(folder);
        });
        return SeamlessImmutable.merge(state, foldersByIdImmutable);
      }

    case FINDER_ASSETS_SEARCH_FETCH:
      {
        const {
          assets
        } = action;
        if (!assets) return state;
        const folders = assets.reduce((list, asset) => {
          if (asset.type === FOLDER) {
            const {
              path: fullPath,
              folderPath
            } = asset,
                  rest = _objectWithoutPropertiesLoose(asset, ["path", "folderPath"]);

            list.push(Object.assign({}, rest, {
              fullPath,
              __folderPath: folderPath
            }));
          }

          return list;
        }, []);
        return mergeFolders(state, folders);
      }

    case FINDER_FOLDER_CREATE:
    case FINDER_FOLDER_DUPLICATE:
      {
        const {
          folder
        } = action;
        if (!folder) return state;
        return state.set(folder.id, new FolderModel(folder));
      }

    case FINDER_FOLDER_RENAME:
      {
        const {
          folderId,
          folder: renamedFolder,
          error
        } = action;

        if (renamedFolder) {
          return state.set(folderId, new FolderModel(renamedFolder));
        }

        const folder = state[folderId];
        if (!folder) return state;
        const folderName = error ? action.oldFolderName : action.folderName;
        return state.setIn([folderId, 'name'], folderName);
      }

    case FINDER_FOLDER_DELETE:
      {
        const {
          folderId,
          contents,
          deleted
        } = action;
        if (!deleted) return state;
        const deletedFolderIds = [folderId, ...contents.subfolders];
        return state.without(deletedFolderIds);
      }

    case FINDER_FOLDER_CONTENTS_BULK_DELETE:
      {
        const {
          subfolders = []
        } = action;
        return subfolders.reduce((updatedState, subfolder) => {
          return updatedState.without(subfolder.id);
        }, state);
      }

    default:
      {
        return state;
      }
  }
}

function isFetchingContentsById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case FINDER_FOLDER_CONTENTS_FETCH:
      {
        const {
          folderId,
          contents,
          error
        } = action;
        return state.merge({
          [folderId]: !(contents || error)
        });
      }

    case FINDER_FOLDER_DELETE:
      {
        const {
          folderId,
          contents,
          deleted
        } = action;
        if (!deleted) return state;
        const deletedFolderIds = [folderId, ...contents.subfolders];
        return state.without(deletedFolderIds);
      }

    case FINDER_OPEN_FOLDERS_FETCH:
      {
        const {
          folderIds,
          contents
        } = action;

        if (!(Array.isArray(folderIds) && folderIds.length)) {
          return state;
        }

        return folderIds.reduce((__state, __folderId) => {
          return __state.set(__folderId, !contents);
        }, state);
      }

    default:
      {
        return state;
      }
  }
}

function defaultsFetchStatus(state = UNINITIALIZED, action) {
  switch (action.type) {
    case FINDER_FILEMAPPER_FETCH_DEFAULT:
      {
        if (action.contents) return SUCCEEDED;
        if (action.error) return FAILED;
        return PENDING;
      }

    default:
      return state;
  }
}

function fetchContentsErrorById(state = {}, action) {
  switch (action.type) {
    case FINDER_FOLDER_CONTENTS_FETCH:
      {
        const {
          folderId,
          error
        } = action;
        return Object.assign({}, state, {
          [folderId]: error || null
        });
      }

    case FINDER_FOLDER_DELETE:
      {
        const {
          folderId,
          contents,
          deleted
        } = action;
        if (!deleted) return state;
        const deletedFolderIds = [folderId, ...contents.subfolders];
        deletedFolderIds.forEach(__folderId => delete state[__folderId]);
        return state;
      }

    default:
      {
        return state;
      }
  }
}

function addFolderAsset(list, assetId) {
  if (!assetId) return list;
  const ids = {};
  const newList = [];
  let hasDupe = false;
  list.forEach(id => {
    if (ids[id]) {
      hasDupe = true;
      return;
    }

    newList.push(id);
    ids[id] = id;
  });

  if (hasDupe === false && ids[assetId]) {
    return list;
  }

  if (!ids[assetId]) {
    return newList.concat(assetId);
  }

  return newList;
}

function addFile(state, folderId, fileId) {
  const contents = state[folderId];
  if (!contents) return state;
  const {
    files
  } = contents;
  const newFiles = addFolderAsset(files, fileId);
  return SeamlessImmutable.setIn(state, [folderId, 'files'], newFiles);
}

function addSubfolder(state, folderId, subfolderId) {
  const contents = state[folderId];
  if (!contents) return state;
  const {
    subfolders
  } = contents;
  const newSubfolders = addFolderAsset(subfolders, subfolderId);
  return SeamlessImmutable.setIn(state, [folderId, 'subfolders'], newSubfolders);
}

function removeFile(state, folderId, fileId) {
  const contents = state[folderId];
  if (!contents) return state;
  const {
    files
  } = contents;
  return SeamlessImmutable.setIn(state, [folderId, 'files'], files.filter(__fileId => __fileId !== fileId));
}

function removeSubfolder(state, folderId, subfolderId) {
  const contents = state[folderId];
  if (!contents) return state;
  const {
    subfolders
  } = contents;
  return SeamlessImmutable.setIn(state, [folderId, 'subfolders'], subfolders.filter(__subfolderId => __subfolderId !== subfolderId));
}

function contentsById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case FINDER_FILEMAPPER_FETCH_DEFAULT:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        const {
          files,
          subfolders
        } = contents;
        subfolders.forEach(subfolder => {
          if (!state[subfolder.parentFolderId]) {
            state = SeamlessImmutable.merge(state, createFolderContents(subfolder.parentFolderId));
          }

          state = addSubfolder(state, subfolder.parentFolderId, subfolder.id);
        });
        files.forEach(file => {
          if (!state[file.folderId]) {
            state = SeamlessImmutable.merge(state, createFolderContents(file.folderId), {
              deep: true
            });
          }

          state = addFile(state, file.folderId, file.id);
        });
        return state;
      }

    case FINDER_FOLDER_CONTENTS_FETCH:
    case FINDER_ROOT_FOLDER_CONTENTS_FETCH:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        let folderContentsById = getFolderContentsByIdFromFolderContentsObject(contents);

        if (action.type === FINDER_ROOT_FOLDER_CONTENTS_FETCH) {
          // Add @hubspot folder as child of root
          folderContentsById = addSubfolder(folderContentsById, contents.folder.id, HUBSPOT_FOLDER_ID);
        }

        return SeamlessImmutable.merge(state, folderContentsById, {
          deep: true
        });
      }

    case FINDER_FOLDER_CONTENTS_BULK_DELETE:
      {
        const {
          files = [],
          subfolders = []
        } = action;
        let updatedState = files.reduce((__state, file) => {
          return removeFile(__state, file.folderId, file.id);
        }, state);
        updatedState = subfolders.reduce((__state, subfolder) => {
          return removeSubfolder(__state, subfolder.folderId, subfolder.id);
        }, updatedState);
        return updatedState;
      }

    case FINDER_OPEN_FOLDERS_FETCH:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        const folderContentsById = contents.reduce((hash, currFolderContents) => {
          const currFolderContentsById = getFolderContentsByIdFromFolderContentsObject(currFolderContents);
          return Object.assign({}, hash, {}, currFolderContentsById);
        }, {});
        return SeamlessImmutable.merge(state, folderContentsById);
      }

    case FINDER_FILE_CREATE:
    case FINDER_FILE_CREATE_FROM_EDITOR:
    case FINDER_FILE_DUPLICATE:
    case FINDER_FILE_DUPLICATE_LAYOUT_TO_TEMPLATE:
    case FINDER_FILE_DUPLICATE_READ_ONLY:
    case FINDER_FILE_RESTORE_DELETED:
      {
        const {
          file
        } = action;
        if (!file) return state;
        return addFile(state, file.folderId, file.id);
      }

    case FINDER_FILE_DELETE:
      {
        const {
          fileId,
          folderId,
          deleted
        } = action;
        if (!(folderId && deleted)) return state;
        return removeFile(state, folderId, fileId);
      }

    case FINDER_FOLDER_CREATE:
    case FINDER_FOLDER_DUPLICATE:
      {
        const {
          folder
        } = action;
        if (!folder) return state;
        return addSubfolder(state, folder.parentFolderId, folder.id);
      }

    case FINDER_FOLDER_DELETE:
      {
        const {
          folderId,
          parentFolderId,
          contents,
          deleted
        } = action;
        if (!deleted) return state;
        const deletedFolderIds = [folderId, ...contents.subfolders];
        let updatedState = SeamlessImmutable.without(state, deletedFolderIds);
        updatedState = removeSubfolder(updatedState, parentFolderId, folderId);
        return updatedState;
      }

    case FINDER_FOLDER_CONTENTS_MOVE:
      {
        const {
          folderId,
          results
        } = action;
        if (!results) return state;
        return results.reduce((__updatedState, asset) => {
          let updatedState = __updatedState;

          if (asset.type === FOLDER) {
            updatedState = addSubfolder(updatedState, folderId, asset.id);

            if (!asset.cloned) {
              updatedState = removeSubfolder(updatedState, asset.parentFolderId, asset.id);
            }
          } else {
            updatedState = addFile(updatedState, folderId, asset.id);

            if (!asset.cloned) {
              updatedState = removeFile(updatedState, asset.parentFolderId, asset.id);
            }
          }

          return updatedState;
        }, state);
      }

    default:
      {
        return state;
      }
  }
}

function openById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case FINDER_OPEN_FOLDERS_FETCH:
      {
        const {
          folderIds = [],
          hubspotFolderIds = []
        } = action;
        const ids = [...folderIds, ...hubspotFolderIds];
        if (!ids.length) return state;
        const newState = {};
        ids.forEach(id => {
          if (id) newState[id] = true;
        });
        return SeamlessImmutable.merge(state, newState);
      }

    case FINDER_FOLDER_TOGGLE_OPEN:
      {
        const {
          folderId,
          open = !state[folderId]
        } = action;
        if (!folderId) return state;
        return open ? SeamlessImmutable.set(state, folderId, true) : SeamlessImmutable.without(state, folderId);
      }

    case FINDER_FOLDERS_COLLAPSE_ALL:
      {
        return EMPTY_OBJECT;
      }

    case FINDER_FOLDER_DELETE:
      {
        const {
          folderId,
          contents,
          deleted
        } = action;
        if (!deleted) return state;
        const deletedFolderIds = [folderId, ...contents.subfolders].filter(Boolean);
        return SeamlessImmutable.without(state, deletedFolderIds);
      }

    case FINDER_FOLDER_OPEN_DEEPLY_NESTED:
      {
        const {
          folderId,
          parentFolderIds
        } = action;
        if (!parentFolderIds) return state;
        const openedFolders = [folderId, ...parentFolderIds];
        const newState = {};
        openedFolders.forEach(id => {
          if (id) newState[id] = true;
        });
        return SeamlessImmutable.merge(state, newState);
      }

    default:
      {
        return state;
      }
  }
}

function folderMetadataById(state = EMPTY_OBJECT, action) {
  switch (action.type) {
    case FINDER_ROOT_FOLDER_CONTENTS_FETCH:
    case FINDER_FOLDER_CONTENTS_FETCH:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        const newFolderMetadataById = getFolderMetadataByIdFromFolderContentsTree(contents);
        return SeamlessImmutable.merge(state, newFolderMetadataById);
      }

    case FINDER_OPEN_FOLDERS_FETCH:
      {
        const {
          contents
        } = action;
        if (!contents) return state;
        const newFolderMetadataById = contents.reduce((hash, currFolderContents) => {
          const currFolderMetadataById = getFolderMetadataByIdFromFolderContentsTree(currFolderContents);
          return Object.assign({}, hash, {}, currFolderMetadataById);
        }, {});
        return SeamlessImmutable.merge(state, newFolderMetadataById);
      }

    case FINDER_FOLDER_TOGGLE_LOCK:
      {
        const {
          folderId,
          newFolderMetadata
        } = action;
        if (!folderId || !newFolderMetadata) return state;
        return SeamlessImmutable.setIn(state, [folderId], newFolderMetadata);
      }

    default:
      {
        return state;
      }
  }
}

export const folders = combineReducers({
  rootFolderId,
  rootFolderContentsRequestStatus,
  fetchRootFolderContentsError,
  byId,
  isFetchingContentsById,
  defaultsFetchStatus,
  fetchContentsErrorById,
  contentsById,
  openById,
  folderMetadataById
});